import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Spinner, Modal, Form, Toast } from 'react-bootstrap';
import { Plus, FileText, CheckCircle2, Calendar, Trash2, XCircle } from 'lucide-react';
// Generated by Copilot — Migrated from Userfront to Clerk
import useAuthHeaders from '../hooks/useAuthHeaders';
import './ListadoRFC.css';

const ListadoRFC = () => {
  const [rfcs, setRfcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newRfc, setNewRfc] = useState('');
  const [savingRfc, setSavingRfc] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingRfc, setDeletingRfc] = useState(false);
  const { userId, getToken } = useAuthHeaders();

  useEffect(() => {
    const fetchRfcs = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/UsersRFC/${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setRfcs(data);
        }
      } catch (error) {
        console.error('Error fetching RFCs:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchRfcs();
    }
  }, [userId, getToken]);

  const handleSaveRfc = async () => {
    if (!newRfc) return;
    
    setSavingRfc(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/UsersRFC`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: 0,
            userId: userId,
            rfc: newRfc.toUpperCase(),
            isDeleted: false
          })
        }
      );

      if (!response.ok) {
        // Handle specific error responses
        if (response.status === 400) {
          const errorData = await response.text();
          throw new Error(errorData);
        }
        throw new Error('Error al guardar el RFC');
      }

      // First close modal and clear input
      setShowModal(false);
      setNewRfc('');

      // Then fetch updated data
      const updatedResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/UsersRFC/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!updatedResponse.ok) {
        throw new Error('Error al actualizar la lista');
      }

      const data = await updatedResponse.json();
      setRfcs(data);
      
      // Set success message and show toast
      setToastMessage('RFC agregado correctamente');
      setToastVariant('success');
      setShowToast(true);
      
    } catch (error) {
      console.error('Error:', error);
      // Set error message and show toast
      setToastMessage(error.message);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setSavingRfc(false);
    }
  };

  const handleDeleteRfc = async () => {
    if (!deleteTarget) return;

    setDeletingRfc(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/UsersRFC/${encodeURIComponent(deleteTarget)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al eliminar el RFC');
      }

      setRfcs(prev => prev.filter(rfc => rfc.rfc !== deleteTarget));
      setDeleteTarget(null);

      setToastMessage('RFC eliminado correctamente');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Error:', error);
      setToastMessage(error.message);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setDeletingRfc(false);
    }
  };

  return (
    <div className="listado-rfc-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Mis RFC</h2>
          <p className="page-subtitle text-muted">Administra tus RFC registrados</p>
        </div>
        <Button 
          className="add-rfc-btn"
          onClick={() => {
            setNewRfc('');
            setShowModal(true);
          }}
        >
          <Plus size={20} className="me-2" />
          Agregar Nuevo RFC
        </Button>
      </div>

      <Card className="rfc-table-card border-0 shadow-sm">
        <Card.Body className="p-4">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" role="status" style={{ color: '#13a0ff' }}>
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
              <p className="mt-3 text-muted">Cargando RFCs...</p>
            </div>
          ) : rfcs.length === 0 ? (
            <div className="empty-state text-center p-5">
              <FileText size={64} className="mb-3" style={{ color: '#13a0ff', opacity: 0.3 }} />
              <h5 className="text-muted">No tienes RFCs registrados</h5>
              <p className="text-muted">Agrega tu primer RFC para comenzar</p>
              <Button 
                variant="primary"
                onClick={() => setShowModal(true)}
                className="mt-3"
              >
                <Plus size={20} className="me-2" />
                Agregar RFC
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="modern-table">
                <thead>
                  <tr>
                    <th><FileText size={16} className="me-2" />RFC</th>
                    <th><CheckCircle2 size={16} className="me-2" />Estado</th>
                    <th><Calendar size={16} className="me-2" />Fecha de creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rfcs.map(rfc => (
                    <tr key={rfc.rfc}>
                      <td className="rfc-cell">{rfc.rfc}</td>
                      <td>
                        <span className="status-badge status-active">
                          <CheckCircle2 size={14} className="me-1" />
                          {rfc.isDeleted ? 'Inactivo' : 'Activo'}
                        </span>
                      </td>
                      <td>{new Date(rfc.createdAt).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setDeleteTarget(rfc.rfc)}
                        >
                          <Trash2 size={16} className="me-1" />
                          Eliminar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>
            <div className="d-flex align-items-center">
              <div className="modal-icon-wrapper me-3">
                <Plus size={24} />
              </div>
              <div>
                <h5 className="mb-0">Agregar Nuevo RFC</h5>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="form-label-modern">RFC</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: XAXX010101000"
                value={newRfc}
                onChange={(e) => setNewRfc(e.target.value)}
                maxLength="13"
                className="form-control-modern"
              />
              <Form.Text className="text-muted">
                {newRfc.length}/13 caracteres
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button
            className="btn-gradient"
            onClick={handleSaveRfc}
            disabled={savingRfc || !newRfc}
          >
            {savingRfc ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : (
              <>
                <CheckCircle2 size={18} className="me-2" />
                Guardar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => !deletingRfc && setDeleteTarget(null)} centered>
        <Modal.Header closeButton={!deletingRfc} className="border-0 pb-0">
          <Modal.Title>
            <div className="d-flex align-items-center">
              <div className="modal-icon-wrapper me-3">
                <Trash2 size={24} />
              </div>
              <div>
                <h5 className="mb-0">Eliminar RFC</h5>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          ¿Seguro que deseas eliminar el RFC <strong>{deleteTarget}</strong>? Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)} disabled={deletingRfc}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteRfc} disabled={deletingRfc}>
            {deletingRfc ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 size={18} className="me-2" />
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={5000}
        autohide
        className={`custom-toast toast-${toastVariant}`}
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          minWidth: '320px',
          zIndex: 9999
        }}
      >
        <Toast.Header closeButton={false} className="border-0">
          <div className="d-flex align-items-center w-100">
            {toastVariant === 'success' ? (
              <CheckCircle2 size={20} className="me-2" />
            ) : (
              <XCircle size={20} className="me-2" />
            )}
            <strong className="me-auto">{toastVariant === 'success' ? 'Éxito' : 'Error'}</strong>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setShowToast(false)}
            ></button>
          </div>
        </Toast.Header>
        <Toast.Body>
          {toastMessage}
        </Toast.Body>
      </Toast>
    </div>
  );
};

export default ListadoRFC;
